import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import AnnouncementBar from "@/components/AnnouncementBar";
import Footer from "@/components/Footer";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "1. Who we are",
    body: (
      <>
        <p>
          This site (<b>revgrow-services.co.uk</b>) is operated by{" "}
          <b>Revgrow Limited</b>, a company registered in England &amp; Wales
          under the trading name RevGrow Services (UK). Our registered office is
          87 Lozells Street, Birmingham B19 2AP, United Kingdom.
        </p>
        <p>
          RevGrow-Services.co.uk is the services arm of revgrow.co.uk and acts
          as the <b>data controller</b> for any personal data collected via this
          website unless stated otherwise.
        </p>
      </>
    ),
  },
  {
    id: "what-we-collect",
    title: "2. What we collect and why",
    body: (
      <>
        <p>We only collect what we need to run the site and respond to enquiries:</p>
        <ul>
          <li>
            <b>Contact form data</b> (name, work email, company, role, interest,
            message) — used to route your enquiry to a strategist and reply.
          </li>
          <li>
            <b>Newsletter opt-ins</b> (email address) — used only if you actively
            subscribe on our owned property TechBytes.org.uk.
          </li>
          <li>
            <b>Usage data</b> (device, browser, IP, pages viewed) — used
            aggregated for analytics if you consent to analytics cookies.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    title: "3. Cookies & similar technologies",
    body: (
      <>
        <p>
          We use a small number of cookies and localStorage entries. You control
          them via our cookie banner and can change your choice at any time by
          clearing site data.
        </p>
        <ul>
          <li>
            <b>Strictly necessary</b> — session integrity, consent preferences.
            Always on.
          </li>
          <li>
            <b>Analytics (optional)</b> — anonymous usage metrics. Off by
            default.
          </li>
          <li>
            <b>Marketing (optional)</b> — campaign attribution and remarketing.
            Off by default.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "legal-basis",
    title: "4. Legal basis for processing (UK GDPR)",
    body: (
      <>
        <p>We rely on:</p>
        <ul>
          <li>
            <b>Legitimate interests</b> — responding to your enquiry, securing
            the site, aggregate analytics.
          </li>
          <li>
            <b>Consent</b> — for optional analytics and marketing cookies, and
            for marketing communications.
          </li>
          <li>
            <b>Contract</b> — where we are executing a signed agreement with
            you.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "sharing",
    title: "5. Who we share data with",
    body: (
      <>
        <p>
          We do not sell your data. We share it only with sub-processors that
          are needed to operate the service:
        </p>
        <ul>
          <li>Cloud hosting &amp; database providers (EU/UK regions where possible).</li>
          <li>Analytics vendors — only if you have consented.</li>
          <li>CRM &amp; email tools used by our sales team to reply to you.</li>
          <li>
            Regulatory or law enforcement bodies where legally required.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "retention",
    title: "6. Retention",
    body: (
      <>
        <p>
          Enquiry data is kept for <b>36 months</b> from your last interaction,
          unless you become a client (in which case it moves under our client
          contract terms). Marketing opt-ins are kept until you unsubscribe.
        </p>
      </>
    ),
  },
  {
    id: "rights",
    title: "7. Your rights",
    body: (
      <>
        <p>Under UK GDPR you have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you</li>
          <li>Have it rectified or erased</li>
          <li>Restrict or object to processing</li>
          <li>Portability of your data</li>
          <li>Withdraw consent at any time</li>
          <li>Complain to the ICO (ico.org.uk) if you believe we have got it wrong</li>
        </ul>
        <p>
          To exercise any right, email{" "}
          <a href="mailto:privacy@revgrow-services.co.uk">privacy@revgrow-services.co.uk</a>.
          We{"\u2019"}ll respond within 30 days.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "8. Contact",
    body: (
      <>
        <p>
          Revgrow Limited
          <br />
          87 Lozells Street, Birmingham B19 2AP, United Kingdom
        </p>
        <p>
          Privacy &amp; DPO: <a href="mailto:privacy@revgrow-services.co.uk">privacy@revgrow-services.co.uk</a>
          <br />
          General: <a href="mailto:sales@revgrow.co.uk">sales@revgrow.co.uk</a>
        </p>
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="relative min-h-screen">
      <AnnouncementBar />
      <Navbar />
      <section className="relative pt-32 md:pt-40 pb-16">
        <div className="absolute inset-0 grid-bg opacity-30 [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
        <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-teal mb-8"
          >
            <ArrowLeft size={14} /> Back to RevGrow
          </Link>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
            Legal · Privacy Policy
          </div>
          <h1 className="font-display font-semibold text-4xl md:text-5xl lg:text-[68px] leading-[0.98] tracking-[-0.02em]">
            Privacy policy.
          </h1>
          <p className="mt-6 text-base text-muted-foreground max-w-2xl leading-relaxed">
            How Revgrow Limited (RevGrow Services UK) collects, uses and protects
            personal data on this website. Last updated{" "}
            <span className="text-foreground">February 2026</span>.
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3 lg:sticky lg:top-28 h-fit">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Contents
            </div>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-muted-foreground hover:text-teal">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <div className="lg:col-span-9 space-y-10">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-32">
                <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mb-4">
                  {s.title}
                </h2>
                <div className="prose-legal">{s.body}</div>
              </section>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .prose-legal p { color: rgba(232,238,242,0.85); font-size: 15px; line-height: 1.7; margin-bottom: 1rem; }
        .prose-legal ul { list-style: disc; padding-left: 1.25rem; margin-bottom: 1rem; }
        .prose-legal li { color: rgba(232,238,242,0.80); font-size: 15px; line-height: 1.7; margin-bottom: 0.35rem; }
        .prose-legal a { color: #00E5C2; text-decoration: underline; text-underline-offset: 3px; }
        .prose-legal b { color: #fff; font-weight: 600; }
      `}</style>
    </main>
  );
}
